import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, ChevronRight, Bell } from 'lucide-react';
import { getUpcomingRides } from '../utils/api';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';

const UpcomingRidesWidget = () => {
  const [upcomingRides, setUpcomingRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpcomingRides();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchUpcomingRides, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchUpcomingRides = async () => {
    try {
      const rides = await getUpcomingRides(5); // Get next 5 rides
      setUpcomingRides(rides);
    } catch (error) {
      console.error('Error fetching upcoming rides:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateLabel = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return 'Today';
      if (isTomorrow(date)) return 'Tomorrow';
      return format(date, 'MMM dd');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (time) => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bell className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Upcoming Scheduled Rides</h2>
        </div>
        {upcomingRides.length > 0 && (
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
            {upcomingRides.length} Upcoming
          </span>
        )}
      </div>

      {upcomingRides.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No upcoming scheduled rides in the next 7 days</p>
        </div>
      ) : (
        <div className="space-y-3">
          {upcomingRides.map((ride) => (
            <div
              key={ride.id}
              className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Date and Time */}
                  <div className="flex items-center space-x-4 mb-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-gray-900">{getDateLabel(ride.date)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-gray-900">{formatTime(ride.time)}</span>
                    </div>
                  </div>

                  {/* Customer */}
                  <div className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">{ride.passengerName}</span>
                    {ride.passengerPhone && (
                      <span className="text-gray-500 ml-2">• {ride.passengerPhone}</span>
                    )}
                  </div>

                  {/* Locations */}
                  <div className="flex items-start space-x-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="text-gray-600">
                      <div className="font-medium">{ride.pickupLocation}</div>
                      <div className="text-gray-500">→ {ride.dropoffLocation}</div>
                    </div>
                  </div>

                  {/* Passengers and Fare */}
                  <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                    <span>{ride.passengers} {ride.passengers === 1 ? 'passenger' : 'passengers'}</span>
                    <span>•</span>
                    <span className="font-semibold text-green-600">${ride.estimatedFare.toFixed(2)}</span>
                  </div>
                </div>

                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingRidesWidget;
